/**
 * A textarea that grows with what is typed into it.
 *
 * These forms carry a clinic's own copy - a doctor's biography, a treatment's
 * risk notes, what the clinic is good at. Every one of them is the material an
 * article is built from, and a four-line box asking for four hundred characters
 * gets four hundred characters written somewhere else and pasted in, or gets a
 * shorter answer than the person had.
 *
 * Two mechanisms, and the CSS one is the real answer:
 *
 *   `field-sizing: content` makes the browser size the control to its content
 *   with no JavaScript at all. Where it exists, the effect below measures a
 *   height the browser has already applied and changes nothing.
 *
 *   The effect covers everything else. `scrollHeight` on a textarea whose own
 *   height is unset reports the content height, so setting the height to it is
 *   one assignment - and doing it on every render rather than only on input is
 *   what makes it correct when the value changes from outside, which is exactly
 *   what happens when the AI fills these fields in.
 */
import {
  useEffect,
  useLayoutEffect,
  useRef,
  type RefObject,
  type TextareaHTMLAttributes,
} from 'react';

/**
 * Layout effect, so the measurement and the paint happen in the same frame.
 *
 * With a plain effect the box renders at its old height and is corrected
 * afterwards, which on a long value is a visible jump. On the server there is no
 * layout to read, so it falls back rather than warning.
 */
const useIsomorphicLayoutEffect = typeof window === 'undefined' ? useEffect : useLayoutEffect;

export function Textarea({
  value,
  /**
   * The height to start at, in lines. Not a maximum - it is what an empty box
   * looks like, so a field expected to hold a paragraph does not begin as a
   * single line and invite one.
   */
  minRows = 3,
  /**
   * The caller's own handle on the element.
   *
   * Needed when an action is about a POSITION inside the box - inserting a
   * variable at the caret rather than appending it to the end. The measurement
   * below uses the same node, so the two cannot disagree about which element this
   * is.
   */
  elementRef,
  ...rest
}: Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'rows' | 'value'> & {
  value: string;
  minRows?: number;
  elementRef?: RefObject<HTMLTextAreaElement | null>;
}) {
  const own = useRef<HTMLTextAreaElement>(null);
  const ref = elementRef ?? own;

  useIsomorphicLayoutEffect(() => {
    const node = ref.current;
    if (!node) return;
    /*
     * Cleared first: scrollHeight never shrinks below the height already set, so
     * measuring without this makes the box grow and never contract.
     */
    /*
     * Where the browser sizes the control itself, leave it alone. An inline
     * height set here WINS over `field-sizing: content`, and this effect only
     * reruns when the value changes - so a box measured while its dialog was
     * still closed kept `height: 0px` after it opened, and 400 characters showed
     * as two clipped lines with a scrollbar.
     */
    if (typeof CSS !== 'undefined' && CSS.supports('field-sizing', 'content')) return;
    node.style.height = 'auto';
    // 0 means it is not laid out yet; keep the CSS floor rather than collapsing.
    if (node.scrollHeight > 0) node.style.height = `${node.scrollHeight}px`;
  }, [value]);

  return (
    <textarea
      {...rest}
      ref={ref}
      value={value}
      rows={minRows}
      className={`grow${rest.className ? ` ${rest.className}` : ''}`}
    />
  );
}
